// src/oura/onboarding/messageText.ts
//
// What is typed into an email's fields, and the check it passes before it is
// sent. Kept apart from the components so the same check runs in the New
// client dialog and in every drawer.

import type { KickoffDraft, MessageDraft } from "@service/aeo/onboarding";
import { cn } from "../lib/cn";
import { formatPeople, parsePeople } from "./drafts";

// 16px on a phone: smaller text makes iPhones zoom in on a tap.
const CONTROL = "w-full rounded-xl bg-white px-3 text-base text-gray-900 sm:text-sm ring-1 ring-inset ring-black/[0.08] placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-blue-600 disabled:bg-gray-50 disabled:text-gray-500";
const INVALID = "ring-rose-400";

export function inputClass(error?: string | null): string {
    return cn("h-10", CONTROL, error && INVALID);
}
export function textareaClass(error?: string | null): string {
    return cn("py-2.5 leading-5", CONTROL, error && INVALID);
}

/** What is typed into the email fields, before it is checked. */
export interface MessageText { to: string; cc: string; subject: string; body: string }
export type MessageErrors = Partial<Record<keyof MessageText, string>>;

export function toMessageText(draft: MessageDraft): MessageText {
    return { to: formatPeople(draft.to), cc: formatPeople(draft.cc), subject: draft.subject, body: draft.body };
}

/** The kick-off has no subject or body, so only who it goes to is typed. */
export function toRecipientText(draft: KickoffDraft): MessageText {
    return { to: formatPeople(draft.to), cc: formatPeople(draft.cc), subject: "", body: "" };
}

/** The kick-off's recipients, checked the way `readMessage` checks them. */
export function readRecipients(text: MessageText): { draft: KickoffDraft; errors: null } | { draft: null; errors: MessageErrors } {
    const to = parsePeople(text.to);
    const cc = parsePeople(text.cc);
    const errors: MessageErrors = {};
    if (to.bad.length > 0) errors.to = `Check this address: ${to.bad[0]}`;
    else if (to.people.length === 0) errors.to = "Add who this goes to.";
    if (cc.bad.length > 0) errors.cc = `Check this address: ${cc.bad[0]}`;
    if (Object.keys(errors).length > 0) return { draft: null, errors };
    return { draft: { to: to.people, cc: cc.people }, errors: null };
}

export function sameMessage(a: MessageText, b: MessageText): boolean {
    return a.to === b.to && a.cc === b.cc && a.subject === b.subject && a.body === b.body;
}

/** Retyping answers an error. Drops the error of every field that changed. */
export function clearFixed(errors: MessageErrors | null, was: MessageText, now: MessageText): MessageErrors | null {
    if (!errors) return errors;
    const left: MessageErrors = {};
    for (const key of Object.keys(errors) as Array<keyof MessageText>) {
        if (was[key] === now[key]) left[key] = errors[key];
    }
    return Object.keys(left).length > 0 ? left : null;
}

/** The checked message, or what is wrong with it. */
export function readMessage(text: MessageText): { draft: MessageDraft; errors: null } | { draft: null; errors: MessageErrors } {
    const to = parsePeople(text.to);
    const cc = parsePeople(text.cc);
    const errors: MessageErrors = {};
    if (to.bad.length > 0) errors.to = `Check this address: ${to.bad[0]}`;
    else if (to.people.length === 0) errors.to = "Add who this goes to.";
    if (cc.bad.length > 0) errors.cc = `Check this address: ${cc.bad[0]}`;
    if (!text.subject.trim()) errors.subject = "Add a subject.";
    if (!text.body.trim()) errors.body = "The email is empty.";
    if (Object.keys(errors).length > 0) return { draft: null, errors };
    return { draft: { to: to.people, cc: cc.people, subject: text.subject.trim(), body: text.body.trim() }, errors: null };
}

/** "https://www.Site.com/about" becomes "site.com". Empty when it is not a website. */
export function cleanDomain(input: string): string {
    const host = input.trim().toLowerCase().replace(/^[a-z]+:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0];
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : "";
}
